// turnTrace.js — per-turn STAGE trace for observability (sprint verify-loop-observability E2).
//
// Pass/fail (E1) tells you THAT a turn was weak; this tells you WHERE. The loop already
// emits a coarse `loop_step`; this adds a STAGE axis so a turn is localizable to the
// canonical loop phases (gather→retrieve→prompt→model→tool→verify). The consumer is the
// honest, granular signal for the verify gate (in-turn) and the learning loop
// (cross-session) — NOT a vanity dashboard.
//
// PURE: no I/O, never throws, `now` injectable for tests. Mirrors turnTelemetry.js style.

export const TURN_STAGES = ['gather', 'retrieve', 'prompt', 'model', 'tool', 'verify'];
const STATUS_RANK = { error: 3, warn: 2, ok: 1, skip: 0 };
const VALID_STATUS = new Set(['ok', 'warn', 'error', 'skip']);

export function createTurnTrace({ now = Date.now() } = {}) {
  return { startedAt: typeof now === 'number' ? now : 0, stages: [] };
}

// markStage(trace, stage, status, detail?, { now }) — append a stage observation.
// status ∈ ok|warn|error|skip. Invalid stage/trace degrade to a no-op (never throws).
export function markStage(trace, stage, status = 'ok', detail = '', { now = Date.now() } = {}) {
  if (!trace || typeof trace !== 'object' || !Array.isArray(trace.stages)) return trace;
  const s = String(stage || '').trim();
  if (!TURN_STAGES.includes(s)) return trace;
  const st = VALID_STATUS.has(status) ? status : 'ok';
  const base = typeof trace.startedAt === 'number' ? trace.startedAt : now;
  trace.stages.push({
    stage: s,
    status: st,
    detail: String(detail || '').slice(0, 200),
    ms: Math.max(0, (typeof now === 'number' ? now : base) - base),
  });
  return trace;
}

// summarizeTrace(trace) → { ok, weakestStage, stages }. weakestStage = the highest-
// severity weak (warn/error) stage; ties broken by the LATEST. ok = no error stage.
// PURE; tolerates a missing/garbage trace (→ ok:true, weakestStage:null).
export function summarizeTrace(trace) {
  const stages = trace && Array.isArray(trace.stages) ? trace.stages : [];
  let weakest = null;
  let weakestRank = 0;
  for (const e of stages) {
    const rank = STATUS_RANK[e?.status] ?? 0;
    if (rank >= STATUS_RANK.warn && rank >= weakestRank) {
      weakestRank = rank;
      weakest = e;
    }
  }
  return {
    ok: !stages.some((e) => e?.status === 'error'),
    weakestStage: weakest ? { stage: weakest.stage, status: weakest.status, detail: weakest.detail } : null,
    stages,
  };
}
